import React, {useState} from 'react';

import DeleteAccountModal from '../../components/modals/DeleteAccountModal';
import {isSiteHeaderActive} from '../../components/SiteHeader/headerDenyList';
import ContentView from '../../components/ui-controls/layout/ContentView';
import FlatListMenu from '../../components/ui-controls/MenuFlatList';
import {handleLogoutPress} from '../../helpers/authHelpers';
import {AppRoute} from '../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {useStores} from '../../stores/use-stores';
import {Analytics} from '../../utils/analytics/analytics';
import {EventName} from '../../utils/analytics/events';
import {logger, serializeError} from '../../utils/logger';
import {rewardsMarketingName} from '../../utils/rewards';

const AccountChooser = ({navigation}: RouteNavigatorScreenProps<AppRoute.ACCOUNT_LOGGED_IN>) => {
  const {userStore} = useStores();

  const [deleteAccountModalVisible, setDeleteAccountModalVisible] = useState(false);

  const logOutText = 'Log Out';

  const handleDeleteAccountModalOK = async () => {
    try {
      const userId = userStore.id;

      await userStore.deleteUser();
      await userStore.logout();

      Analytics.logEvent({
        eventName: EventName.DeleteAccount,
        parameters: {
          user_id: userId,
        },
      });
      Analytics.clearUserSession();
    } catch (err) {
      logger.error('AccountChooser', 'Error deleting user', serializeError(err));
    }
  };

  const handleDeleteAccountModalCancel = () => setDeleteAccountModalVisible(false);

  const handleMenuItemPress = (menuItem: {target?: AppRoute; name: string}) => {
    if (menuItem.name === logOutText) {
      handleLogoutPress(navigation, userStore);
    } else if (menuItem.target) {
      // Every row's route takes no params; the typed navigate has no overload for a bare union.
      navigation.navigate(menuItem.target as never);
    }
  };

  // DIS-2031: the web SiteHeader account panel (BrandHeader memberItems) already offers the
  // rewards card, Purchase History and Log Out, so web drops them here. Native has no panel.
  const hasAccountPanel = isSiteHeaderActive();
  const menuItems = [
    {target: AppRoute.ACCOUNT_DETAIL, name: 'Update Account'},
    {target: AppRoute.ACCOUNT_CHANGE_PASSWORD, name: 'Change Password'},
    ...(hasAccountPanel ? [] : [{target: AppRoute.ACCOUNT_REWARDS, name: rewardsMarketingName}]),
    {
      target: AppRoute.ACCOUNT_COMMUNICATIONS_PREFERENCES,
      name: 'Communications Preferences',
    },
    {target: AppRoute.ACCOUNT_PAYMENT_METHOD, name: 'Payment Method'},
    ...(hasAccountPanel
      ? []
      : [{target: AppRoute.ACCOUNT_RECENT_ORDERS, name: 'Purchase History'}]),
    {target: AppRoute.ACCOUNT_DELETE, name: 'Delete My Account'},
    ...(hasAccountPanel ? [] : [{name: logOutText}]),
  ];

  return (
    <ContentView>
      <FlatListMenu data={menuItems} onPressItem={handleMenuItemPress} />
      <DeleteAccountModal
        open={deleteAccountModalVisible}
        okCallback={handleDeleteAccountModalOK}
        cancelCallback={handleDeleteAccountModalCancel}
      />
    </ContentView>
  );
};

export default AccountChooser;
